import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GuestOverlay from "@/components/auth/GuestOverlay";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { supabase } from "@/integrations/supabase/client";
import { checkTaskStatus } from "@/lib/task-checker";
import {
  Image,
  Video,
  Upload,
  Sparkles,
  Loader2,
  Coins,
  AlertTriangle,
  CheckCircle2,
  History,
  X,
  RefreshCw,
  ArrowRight,
  Play,
  ImageIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

const REQUIRED_CREDITS = 60;

const Transform = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { generate, isGenerating, userCredits } = useGeneration();

  const [promptText, setPromptText] = useState("motion timelapse building with engineer from photo1 to photo2");
  const [startImage, setStartImage] = useState<File | null>(null);
  const [endImage, setEndImage] = useState<File | null>(null);
  const [selectedRatio, setSelectedRatio] = useState("9:16");

  // Recent generations state
  interface GenerationLog {
    id: string;
    prompt: string;
    display_prompt?: string | null;
    result_url: string | null;
    status: string;
    created_at: string;
    model: string;
    credits_used?: number;
  }

  const [recentGenerations, setRecentGenerations] = useState<GenerationLog[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

  // Gallery state
  const [showGallery, setShowGallery] = useState<"start" | "end" | null>(null);
  const [galleryPhotos, setGalleryPhotos] = useState<{ id: string; url: string; prompt: string }[]>([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [startImageUrl, setStartImageUrl] = useState<string | null>(null);
  const [endImageUrl, setEndImageUrl] = useState<string | null>(null);

  const aspectOptions = [
    { value: "16:9", label: t("แนวนอน", "Landscape") },
    { value: "9:16", label: t("แนวตั้ง", "Portrait") },
  ];

  // Fetch recent generations
  const fetchRecentGenerations = async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, display_prompt, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "video")
        .ilike("model", "%veo%")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentGenerations(data as GenerationLog[]);
    } catch (err) {
      console.error("Error fetching recent generations:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch photos for gallery
  const fetchGalleryPhotos = async () => {
    if (!user) return;
    setLoadingGallery(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, result_url")
        .eq("user_id", user.id)
        .eq("service_type", "photo")
        .eq("status", "completed")
        .not("result_url", "is", null)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      const photos = (data || []).map(item => {
        let url = item.result_url || "";
        // Parse JSON result if needed
        if (url.startsWith("{")) {
          try {
            const parsed = JSON.parse(url);
            url = parsed.resultUrls?.[0] || parsed.url || url;
          } catch { }
        }
        return {
          id: item.id,
          url: url,
          prompt: item.prompt || ""
        };
      }).filter(p => p.url.startsWith("http"));

      setGalleryPhotos(photos);
    } catch (err) {
      console.error("Error fetching gallery photos:", err);
    } finally {
      setLoadingGallery(false);
    }
  };

  const openGallery = (type: "start" | "end") => {
    setShowGallery(type);
    fetchGalleryPhotos();
  };

  const selectFromGallery = (url: string) => {
    if (showGallery === "start") {
      setStartImageUrl(url);
      setStartImage(null); // Clear file if using URL
    } else if (showGallery === "end") {
      setEndImageUrl(url);
      setEndImage(null); // Clear file if using URL
    }
    setShowGallery(null);
  };

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (user) {
      fetchRecentGenerations();
    }
  }, [user]);

  // Realtime subscription for callback notifications
  const subscriptionRef = useRef<any>(null);
  const processedCallbacksRef = useRef<Set<string>>(new Set());

  const handleRealtimeUpdate = useCallback((payload: any) => {
    if (!payload?.new) return;
    const updated = payload.new;
    const old = payload.old;

    if (updated.status === 'completed' &&
      updated.result_url &&
      updated.result_url.startsWith('http') &&
      old?.status !== 'completed') {
      if (processedCallbacksRef.current.has(updated.id)) return;
      processedCallbacksRef.current.add(updated.id);

      if (processedCallbacksRef.current.size > 20) {
        const arr = Array.from(processedCallbacksRef.current);
        processedCallbacksRef.current = new Set(arr.slice(-10));
      }

      toast({
        title: t("Video Ready!", "Video Ready!"),
        description: t("คลิกเพื่อดูผลลัพธ์", "Click to view result"),
        action: (
          <ToastAction
            altText={t("ดูประวัติ", "View history")}
            onClick={() => navigate("/history")}
          >
            {t("ดูประวัติ", "History")}
          </ToastAction>
        ),
        duration: 10000,
      });

      fetchRecentGenerations();
    }
  }, [navigate, t, toast]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel('transform-callbacks')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'generation_logs',
          filter: `user_id=eq.${user.id}`
        },
        handleRealtimeUpdate
      )
      .subscribe();

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [user, handleRealtimeUpdate]);

  const hasEnoughCredits = authLoading || userCredits === undefined ? true : userCredits >= REQUIRED_CREDITS;

  const handleStartImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: t("ไฟล์ไม่ถูกต้อง", "Invalid file"),
          description: t("กรุณาเลือกไฟล์รูปภาพ", "Please select an image file"),
        });
        return;
      }
      setStartImage(file);
    }
  };

  const handleEndImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: t("ไฟล์ไม่ถูกต้อง", "Invalid file"),
          description: t("กรุณาเลือกไฟล์รูปภาพ", "Please select an image file"),
        });
        return;
      }
      setEndImage(file);
    }
  };

  const handleGenerate = async () => {
    // Check if we have images (either from file upload or from gallery URL)
    const hasStartImage = startImage || startImageUrl;
    const hasEndImage = endImage || endImageUrl;

    if (!hasStartImage || !hasEndImage) {
      toast({
        variant: "destructive",
        title: t("กรุณาเลือกรูปภาพ", "Please select images"),
        description: t("ต้องเลือกทั้งรูปเริ่มต้นและรูปสิ้นสุด", "Both start and end images are required"),
      });
      return;
    }

    if (!promptText.trim()) {
      toast({
        variant: "destructive",
        title: t("กรุณากรอก Prompt", "Please enter a prompt"),
        description: t("กรุณาระบุคำอธิบายวิดีโอที่ต้องการ", "Please describe the video you want"),
      });
      return;
    }

    // Upload both images first
    const uploadImage = async (file: File): Promise<string | null> => {
      if (!user) return null;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('generation-inputs')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('generation-inputs')
        .getPublicUrl(filePath);

      return publicUrl;
    };

    try {
      // If using file uploads, upload them first
      // If using gallery URLs, use them directly
      let startUrl = startImageUrl;
      let endUrl = endImageUrl;

      if (startImage) {
        startUrl = await uploadImage(startImage);
      }
      if (endImage) {
        endUrl = await uploadImage(endImage);
      }

      if (!startUrl || !endUrl) {
        toast({
          variant: "destructive",
          title: t("อัพโหลดล้มเหลว", "Upload failed"),
          description: t("ไม่สามารถอัพโหลดรูปภาพได้", "Could not upload images"),
        });
        return;
      }

      // Call the generation with both image URLs
      const result = await generate(
        "video",
        "Veo 3.1 Fast (Transform)",
        promptText,
        REQUIRED_CREDITS,
        {
          ratio: selectedRatio,
          imageUrls: [startUrl, endUrl],
          generationType: "FIRST_AND_LAST_FRAMES_2_VIDEO"
        }
      );

      if (result.success) {
        setStartImage(null);
        setEndImage(null);
        setStartImageUrl(null);
        setEndImageUrl(null);
        fetchRecentGenerations();
      }

      // Scroll to history
      setTimeout(() => {
        const historySection = document.getElementById('recent-history-section');
        if (historySection) {
          historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    } catch (error) {
      console.error('Generation error:', error);
    }
  };

  const handleCheckStatus = async (logId: string) => {
    setCheckingStatus(logId);
    try {
      await checkTaskStatus(logId);
      await fetchRecentGenerations();
    } finally {
      setCheckingStatus(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {!user && <GuestOverlay />}

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4">
              <Sparkles className="h-3 w-3 mr-1" />
              Veo 3.1 Transform
            </Badge>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              <span className="text-gradient">{t("สร้างคลิป สร้างบ้าน Time lapse", "Build House Time Lapse")}</span>
            </h1>
            <p className="text-muted-foreground">
              {t(
                "สร้างวิดีโอเปลี่ยนฉากจากรูปเริ่มต้นไปยังรูปสิ้นสุด",
                "Generate transition videos from start image to end image"
              )}
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Image Upload Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Start Image Card */}
              <Card variant="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Play className="h-4 w-4 text-green-500" />
                    </div>
                    {t("รูปเริ่มต้น", "Photo Start")}
                  </CardTitle>
                  <CardDescription>
                    {t("รูปภาพที่จะเป็นเฟรมแรกของวิดีโอ", "The image that will be the first frame")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <input
                    type="file"
                    id="start-image-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={handleStartImageUpload}
                  />

                  {startImage ? (
                    <div className="relative rounded-lg overflow-hidden border border-border group/image">
                      <img
                        src={URL.createObjectURL(startImage)}
                        alt="Start Frame"
                        className="w-full h-auto max-h-48 object-contain bg-black/5 dark:bg-white/5"
                      />
                      <div className="absolute top-2 right-2">
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover/image:opacity-100 transition-opacity"
                          onClick={() => setStartImage(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="bg-green-500/80 text-white">
                          {t("เฟรมเริ่มต้น", "Start Frame")}
                        </Badge>
                      </div>
                    </div>
                  ) : startImageUrl ? (
                    <div className="relative rounded-lg overflow-hidden border border-border group/image">
                      <img
                        src={startImageUrl}
                        alt="Start Frame"
                        className="w-full h-auto max-h-48 object-contain bg-black/5 dark:bg-white/5"
                      />
                      <div className="absolute top-2 right-2">
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover/image:opacity-100 transition-opacity"
                          onClick={() => setStartImageUrl(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="bg-green-500/80 text-white">
                          {t("จาก Gallery", "From Gallery")}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="start-image-upload"
                      className="block border-2 border-dashed border-green-500/30 rounded-lg p-6 text-center hover:border-green-500/50 transition-colors cursor-pointer bg-green-500/5 hover:bg-green-500/10 h-40 flex flex-col items-center justify-center"
                    >
                      <Upload className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <p className="text-sm text-muted-foreground">
                        {t("คลิกเพื่ออัพโหลดรูปเริ่มต้น", "Click to upload start image")}
                      </p>
                    </label>
                  )}

                  {/* Gallery Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 border-green-500/30 text-green-600 hover:bg-green-500/10"
                    onClick={() => openGallery("start")}
                  >
                    <ImageIcon className="h-4 w-4" />
                    {t("ใช้ภาพจาก Gallery", "Use from Gallery")}
                  </Button>
                </CardContent>
              </Card>

              {/* End Image Card */}
              <Card variant="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-red-500" />
                    </div>
                    {t("รูปสิ้นสุด", "Photo End")}
                  </CardTitle>
                  <CardDescription>
                    {t("รูปภาพที่จะเป็นเฟรมสุดท้ายของวิดีโอ", "The image that will be the last frame")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <input
                    type="file"
                    id="end-image-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={handleEndImageUpload}
                  />

                  {endImage ? (
                    <div className="relative rounded-lg overflow-hidden border border-border group/image">
                      <img
                        src={URL.createObjectURL(endImage)}
                        alt="End Frame"
                        className="w-full h-auto max-h-48 object-contain bg-black/5 dark:bg-white/5"
                      />
                      <div className="absolute top-2 right-2">
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover/image:opacity-100 transition-opacity"
                          onClick={() => setEndImage(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="bg-red-500/80 text-white">
                          {t("เฟรมสิ้นสุด", "End Frame")}
                        </Badge>
                      </div>
                    </div>
                  ) : endImageUrl ? (
                    <div className="relative rounded-lg overflow-hidden border border-border group/image">
                      <img
                        src={endImageUrl}
                        alt="End Frame"
                        className="w-full h-auto max-h-48 object-contain bg-black/5 dark:bg-white/5"
                      />
                      <div className="absolute top-2 right-2">
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover/image:opacity-100 transition-opacity"
                          onClick={() => setEndImageUrl(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="bg-red-500/80 text-white">
                          {t("จาก Gallery", "From Gallery")}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="end-image-upload"
                      className="block border-2 border-dashed border-red-500/30 rounded-lg p-6 text-center hover:border-red-500/50 transition-colors cursor-pointer bg-red-500/5 hover:bg-red-500/10 h-40 flex flex-col items-center justify-center"
                    >
                      <Upload className="h-8 w-8 mx-auto mb-2 text-red-500" />
                      <p className="text-sm text-muted-foreground">
                        {t("คลิกเพื่ออัพโหลดรูปสิ้นสุด", "Click to upload end image")}
                      </p>
                    </label>
                  )}

                  {/* Gallery Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 border-red-500/30 text-red-600 hover:bg-red-500/10"
                    onClick={() => openGallery("end")}
                  >
                    <ImageIcon className="h-4 w-4" />
                    {t("ใช้ภาพจาก Gallery", "Use from Gallery")}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Arrow between cards on mobile */}
            <div className="flex justify-center md:hidden">
              <ArrowRight className="h-8 w-8 text-muted-foreground rotate-90" />
            </div>

            {/* Prompt and Settings Card */}
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="text-lg">{t("คำอธิบายการเปลี่ยนฉาก", "Transition Description")}</CardTitle>
                <CardDescription>
                  {t("อธิบายวิดีโอที่ต้องการสร้างระหว่างรูปเริ่มต้นและรูปสิ้นสุด", "Describe the video transition between start and end images")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Prompt Input */}
                <Textarea
                  placeholder={t("ใส่คำอธิบายวิดีโอ...", "Enter video description...")}
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  rows={3}
                  className="resize-none"
                />

                {/* Aspect Ratio Selection */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t("อัตราส่วนภาพ", "Aspect Ratio")}
                  </label>
                  <div className="flex gap-2">
                    {aspectOptions.map((option) => (
                      <Button
                        key={option.value}
                        variant={selectedRatio === option.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedRatio(option.value)}
                      >
                        {option.label} ({option.value})
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Credits Info */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-primary" />
                    <span className="font-medium">{REQUIRED_CREDITS} {t("เครดิต", "credits")}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t("คุณมี", "You have")} <span className="font-bold text-foreground">{userCredits}</span> {t("เครดิต", "credits")}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  variant="hero"
                  size="lg"
                  className="w-full"
                  onClick={handleGenerate}
                  disabled={isGenerating || !hasEnoughCredits || !(startImage || startImageUrl) || !(endImage || endImageUrl)}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {t("กำลังสร้าง...", "Generating...")}
                    </>
                  ) : !hasEnoughCredits ? (
                    <>
                      <AlertTriangle className="h-5 w-5 mr-2" />
                      {t("เครดิตไม่เพียงพอ", "Insufficient Credits")}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 mr-2" />
                      {t("เริ่มสร้างวิดีโอ", "Start Generation")}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Recent History Section */}
            <div id="recent-history-section">
              <Card variant="glass">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="h-5 w-5" />
                      {t("ประวัติล่าสุด", "Recent History")}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchRecentGenerations}
                      disabled={loadingHistory}
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : recentGenerations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{t("ยังไม่มีประวัติการสร้าง", "No generation history yet")}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentGenerations.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          {/* Thumbnail or Status */}
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            {log.status === 'completed' && log.result_url?.startsWith('http') ? (
                              <video
                                src={log.result_url}
                                className="w-full h-full object-cover"
                                muted
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {log.status === 'processing' ? (
                                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                ) : log.status === 'failed' ? (
                                  <AlertTriangle className="h-6 w-6 text-destructive" />
                                ) : (
                                  <Video className="h-6 w-6 text-muted-foreground" />
                                )}
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {log.display_prompt || log.prompt}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge
                                variant={
                                  log.status === 'completed' ? 'default' :
                                    log.status === 'processing' ? 'secondary' : 'destructive'
                                }
                                className="text-xs"
                              >
                                {log.status === 'completed' ? t("สำเร็จ", "Completed") :
                                  log.status === 'processing' ? t("กำลังสร้าง", "Processing") :
                                    t("ล้มเหลว", "Failed")}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(log.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {log.status === 'processing' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCheckStatus(log.id)}
                                disabled={checkingStatus === log.id}
                              >
                                {checkingStatus === log.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            {log.status === 'completed' && log.result_url?.startsWith('http') && (
                              <Button
                                variant="outline"
                                size="sm"
                                asChild
                              >
                                <a href={log.result_url} target="_blank" rel="noopener noreferrer">
                                  {t("ดู", "View")}
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Gallery Modal */}
      {showGallery && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowGallery(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">
                    {t("เลือกภาพจาก Gallery", "Select from Gallery")}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {showGallery === "start"
                      ? t("เลือกภาพสำหรับเฟรมเริ่มต้น", "Select image for start frame")
                      : t("เลือกภาพสำหรับเฟรมสิ้นสุด", "Select image for end frame")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowGallery(null)}
                  className="rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingGallery ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : galleryPhotos.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t("ยังไม่มีรูปภาพใน Gallery", "No photos in gallery yet")}</p>
                  <p className="text-sm mt-1">{t("สร้างรูปภาพที่หน้า Generate Photo ก่อน", "Generate photos first on the Photo page")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                  {galleryPhotos.map((photo) => (
                    <button
                      key={photo.id}
                      className="relative aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-primary transition-all group"
                      onClick={() => selectFromGallery(photo.url)}
                    >
                      <img
                        src={photo.url}
                        alt={photo.prompt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <CheckCircle2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border bg-muted/30">
              <p className="text-xs text-muted-foreground text-center">
                {t("แสดง 20 รูปล่าสุด", "Showing last 20 photos")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transform;
